import {
  Badge,
  type BadgeColor,
  BadgePopover,
  DatePicker,
  type DatePickerValue,
  DetailCard,
  datePickerValueToDateString,
  GridCellText,
  Icon,
  Select,
  stringToDatePickerValue,
  type TableColumn,
} from '@civilgrid/ui/components';
import { OPERATOR_TYPES } from '@civilgrid/ui/utils';
import {
  formatConflictDetail,
  formatScheduleOverlap,
  formatScheduleOverlapParts,
} from '@civilgrid/utils/src/intelligence/conflictWindows';
import { Link } from '@tanstack/react-router';
import type { CellContext } from '@tanstack/react-table';
import { nanoid } from 'nanoid';
import { type DependencyList, type ReactNode, useId, useMemo } from 'react';
import { useAsyncFn } from 'react-use';
import type { BulkConstructionDatesUpdates } from '~/api/intelligenceSummariesV3';
import { editProject as apiEditProject, updateIntelligenceSummary as apiUpdateSummary } from '~/api/projects2';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { rowBadgeLabel } from '~/common/rowBadgeLabel';
import { type Agency, ConflictRec, type IntelligenceSummary, type PlanStatus } from '~/common/types';
import { Notify } from '~/components2/Notify';
import type { ExportableColumnDef } from '~/components2/Table';
import FreeFormTextCell from '~/components2/Table/Cells/FreeFormTextCell';
import { getSummaryValue, toMiles } from '../conflictsSummary/common';
import { sceColumns, sceHeaderTooltips } from '../conflictsSummary/customColumns';
import { getBeforeSnapshot } from './analytics';
import { Cell, CellSkeleton, ComputeStateCell } from './cells';
import {
  datePickerValueToYear,
  type InlineDateField,
  toInlineDateUpdates,
  toLocalDate,
  yearToDatePickerValue,
} from './useBulkEditConstructionDates';
import { formatPercentConflict } from './utils';

/** Callbacks for the editable cells (ServerTable owns its table instance, so `table.options.meta` isn't available). */
export type SummaryColumnCallbacks = {
  /** Invalidate summary queries after a successful cell edit. */
  onEdited: () => void;
  /** Register a summary for compute-status polling (date edits trigger a recompute). */
  addToPoll: (summaryId: string) => void;
  /** Apply an inline date-cell edit to a single summary's project (server derives unset dates). */
  updateDates: (summaryId: string, updates: BulkConstructionDatesUpdates) => Promise<void>;
};

export type SummaryColumn = TableColumn<IntelligenceSummary>;

/** Compact indicator beside an editor whose last save failed (the toast carries the reason). */
const CellError = ({ 'data-testid': dataTestId }: { 'data-testid'?: string }) => {
  return (
    <span className="shrink-0 text-xs italic text-red-500" data-testid={dataTestId}>
      Save failed
    </span>
  );
};

/**
 * Save wrapper for editable cells: toasts the server's rejection reason (400s
 * carry `{ reason }`) and rethrows so the cell shows its error indicator. The
 * editor must stay mounted on failure — the next attempt clears the error.
 */
function useCellSave<Args extends unknown[]>(save: (...args: Args) => Promise<void>, deps: DependencyList) {
  return useAsyncFn(async (...args: Args) => {
    try {
      await save(...args);
    } catch (e) {
      const reason = (e as { response?: { data?: { reason?: string } } })?.response?.data?.reason;
      Notify.error({ message: reason || 'An error occurred while saving.' });
      throw e;
    }
  }, deps);
}

/** Grid typography wrapper for composite cells that can't render a GridCellText directly. */
const CellShell = ({ children }: { children: ReactNode }) => {
  return <div className="w-full font-noto text-xs font-normal text-grey-90">{children}</div>;
};

type BadgeCellTooltip = { title: ReactNode; description: ReactNode };

/** Badge wrapping a GridCellText. With a tooltip, the badge is a button that opens it as a card. */
export const BadgeCell = ({
  color,
  label,
  tooltip,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: {
  color: BadgeColor;
  label: ReactNode;
  tooltip?: BadgeCellTooltip;
  /** Names a badge repeated per row; it must contain the visible label. */
  'aria-label'?: string;
  'data-testid'?: string;
}) => {
  if (!tooltip) {
    return (
      <Badge color={color} data-testid={dataTestId}>
        <GridCellText>{label}</GridCellText>
      </Badge>
    );
  }

  return (
    <BadgeCellWithCard color={color} label={label} tooltip={tooltip} aria-label={ariaLabel} data-testid={dataTestId} />
  );
};

const BadgeCellWithCard = ({
  color,
  label,
  tooltip,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: {
  color: BadgeColor;
  label: ReactNode;
  tooltip: BadgeCellTooltip;
  'aria-label'?: string;
  'data-testid'?: string;
}) => {
  const titleId = useId();
  const descriptionId = useId();

  return (
    <BadgePopover
      cardLabelledBy={titleId}
      cardDescribedBy={descriptionId}
      content={
        <DetailCard className="w-72">
          <div id={titleId} className="font-semibold">
            {tooltip.title}
          </div>
          <div id={descriptionId}>{tooltip.description}</div>
        </DetailCard>
      }
    >
      <Badge asChild color={color} aria-label={ariaLabel} data-testid={dataTestId}>
        {/* Restores Badge's data-slot, which the popover trigger's own data-slot replaces. */}
        <button type="button" data-slot="badge">
          <GridCellText>{label}</GridCellText>
        </button>
      </Badge>
    </BadgePopover>
  );
};

const recommendationLabels: { [rec in ConflictRec]: string } = {
  [ConflictRec.Accelerate]: 'Accelerate',
  [ConflictRec.Defer]: 'Defer',
  [ConflictRec.NeedsReview]: 'Needs Review',
  [ConflictRec.NoScheduleConflict]: 'No Schedule Conflict',
};

/** Chip color for a schedule recommendation: Defer red, No Schedule Conflict green, others yellow. */
export function recommendationChipVariant(r: ConflictRec): 'yellow' | 'green' | 'red' | 'blue' {
  if (r === ConflictRec.Defer) return 'red';
  if (r === ConflictRec.NoScheduleConflict) return 'green';
  if (r === ConflictRec.Accelerate) return 'blue';
  return 'yellow';
}

function hasWarning(agencies: Agency[]) {
  return !!agencies?.find((a) => a.type === 'Paving Projects' || a.type === 'Moratoriums' || a.type === 'CIP');
}

function getConflictArea(summary: IntelligenceSummary): number {
  return Math.round(summary.conflictArea ?? 0);
}

function getJurisdictionCount(summary: IntelligenceSummary): number {
  return Math.round(
    (summary.incorporatedPlacesCount ?? 0) +
      (summary.countyCount ?? 0) +
      (summary.landUseTransportationDistrictBoundariesCount ?? 0),
  );
}

const YEAR_RANGE = { start: 2020, end: 2040 };

/**
 * Inline-editable construction date cell: a single-record update through the
 * bulk endpoint, so the server derives the unset dates. No clear affordance —
 * dates can be replaced but not removed from the cell.
 */
export function InlineDateCell({
  cell,
  field,
  testIdPrefix,
  callbacks,
}: {
  cell: CellContext<IntelligenceSummary, unknown>;
  field: InlineDateField;
  testIdPrefix: string;
  callbacks: SummaryColumnCallbacks;
}) {
  const summary = cell.row.original;
  const projectId = summary.project.id;
  const summaryId = summary.id;

  const [update, saveDate] = useCellSave(
    async (value: DatePickerValue) => {
      const picked = field === 'year' ? datePickerValueToYear(value) : datePickerValueToDateString(value);
      const updates = toInlineDateUpdates(field, picked);
      if (Object.keys(updates).length === 0) return;

      // Snapshot the pre-edit dates and conflict state before the recompute overwrites them.
      const beforeSnapshot = getBeforeSnapshot(summary);
      const oldStartDate = summary.project.constructionStartDate?.toISODate() ?? null;
      const oldEndDate = summary.project.constructionEndDate?.toISODate() ?? null;

      await callbacks.updateDates(summaryId, updates);
      callbacks.addToPoll(summaryId);
      callbacks.onEdited();

      capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryConstructionDateChanged, {
        project_id: projectId,
        summary_id: summaryId,
        field,
        new_value: updates.constructionYear ?? updates.constructionStartDate ?? updates.constructionEndDate ?? null,
        date_edit_id: nanoid(),
        old_start_date: oldStartDate,
        old_end_date: oldEndDate,
        ...beforeSnapshot,
      });
    },
    [projectId, summaryId, field, callbacks, summary],
  );

  // Memoized on the primitive value: grid re-renders (poll ticks) must keep the
  // reference stable, or the open picker's browsed month resets to the value's.
  const rawValue =
    field === 'year'
      ? summary.project.constructionYear
        ? String(summary.project.constructionYear)
        : ''
      : ((field === 'startDate'
          ? summary.project.constructionStartDate
          : summary.project.constructionEndDate
        )?.toISODate() ?? '');
  const pickerValue = useMemo(
    () => (field === 'year' ? yearToDatePickerValue(rawValue) : stringToDatePickerValue(rawValue)),
    [field, rawValue],
  );

  const minIso = field === 'endDate' ? (summary.project.constructionStartDate?.toISODate() ?? '') : '';
  const minDate = useMemo(() => (minIso ? toLocalDate(minIso) : undefined), [minIso]);

  if (update.loading) return <CellSkeleton data-testid={`${testIdPrefix}-value-${projectId}`} />;

  const yearViewProps =
    field === 'year' ? { multiView: true, views: ['Year' as const], defaultView: 'Year' as const } : {};

  return (
    <CellShell>
      <div className="flex items-center gap-1" data-testid={`${testIdPrefix}-value-${projectId}`}>
        <DatePicker
          placeholder={field === 'year' ? 'Select Year' : 'Select Date'}
          yearRange={YEAR_RANGE}
          allowClear={false}
          triggerVariant="text"
          minDate={minDate}
          value={pickerValue}
          onChange={saveDate}
          className="w-full"
          inputClassName="h-6 px-1"
          data-testid={`${testIdPrefix}-input-${projectId}`}
          {...yearViewProps}
        />
        {update.error && <CellError data-testid={`${testIdPrefix}-error-${projectId}`} />}
      </div>
    </CellShell>
  );
}

/**
 * Legacy display names for SCE columns whose exportLabel differs from what the
 * old table's JSX header actually showed on screen.
 */
const SCE_HEADER_OVERRIDES: Record<string, string> = {
  landAcquisition: 'Land Acquisition',
};

const YES_NO_FILTER = {
  filterValueConfig: {
    type: 'select' as const,
    options: [
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' },
    ],
  },
  filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
};

const WITHIN_100_FILTER = {
  filterValueConfig: {
    type: 'select' as const,
    options: [
      { value: "Within 100'", label: "Within 100'" },
      { value: 'No', label: 'No' },
    ],
  },
  filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
};

const TEXT_FILTER = {
  filterValueConfig: { type: 'text' as const },
  filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
};

const COMPARISON_FILTER = {
  filterValueConfig: { type: 'text' as const },
  filterOperatorTypeConfig: OPERATOR_TYPES.COMPARISON,
};

/**
 * SCE columns with server-side sort/filter, each backed by a matching
 * propertyOverrides expression in the v3 controller.
 */
const SCE_SERVER_FILTERS: Record<string, typeof TEXT_FILTER | typeof YES_NO_FILTER> = {
  city: TEXT_FILTER,
  county: TEXT_FILTER,
  stateDOT: TEXT_FILTER,
  federalLands: TEXT_FILTER,
  restrictedLands: YES_NO_FILTER,
  wetlands: YES_NO_FILTER,
  conservationLands: YES_NO_FILTER,
  envirostor: WITHIN_100_FILTER,
  geotracker: WITHIN_100_FILTER,
  californiaStateParkBoundaries: YES_NO_FILTER,
  federallyRecognizedTribalLands: YES_NO_FILTER,
  caltrans: YES_NO_FILTER,
  coastalCommission: YES_NO_FILTER,
  landAcquisition: COMPARISON_FILTER,
  riskScore: COMPARISON_FILTER,
  program: TEXT_FILTER,
  pif: TEXT_FILTER,
  circuit: TEXT_FILTER,
  leader: TEXT_FILTER,
  region: TEXT_FILTER,
  workType: TEXT_FILTER,
  ugMiles: COMPARISON_FILTER,
  ohDeMiles: COMPARISON_FILTER,
};

/**
 * Adapt an SCE column tree for the ServerTable: GridHead only renders string
 * header labels, so JSX tooltip headers become their label text with the
 * tooltip content re-attached through `meta.headerTooltip` on leaf columns
 * (group headers have no tooltip support).
 */
function toSceColumn(def: ExportableColumnDef<IntelligenceSummary>): SummaryColumn {
  const { columns, ...rest } = def as ExportableColumnDef<IntelligenceSummary> & {
    columns?: ExportableColumnDef<IntelligenceSummary>[];
  };
  const serverFilter = SCE_SERVER_FILTERS[def.id];
  const headerTooltip = columns ? undefined : sceHeaderTooltips[def.id];
  const meta = {
    ...(rest as { meta?: object }).meta,
    ...serverFilter,
    ...(headerTooltip ? { headerTooltip } : {}),
  };
  // Leaf cells render plain strings — wrap them in GridCellText so they match the grid typography.
  const originalCell = rest.cell;
  const gridTextCell = (ctx: CellContext<IntelligenceSummary, unknown>) => (
    <GridCellText>
      {typeof originalCell === 'function' ? originalCell(ctx) : (ctx.getValue() as ReactNode)}
    </GridCellText>
  );
  return {
    ...rest,
    header: typeof rest.header === 'string' ? rest.header : (SCE_HEADER_OVERRIDES[def.id] ?? def.exportLabel ?? def.id),
    enableSorting: !!serverFilter,
    enableColumnFilter: !!serverFilter,
    ...(Object.keys(meta).length ? { meta } : {}),
    // Leaf values come from the server-resolved `sce` payload, not the legacy client-side accessors.
    ...(columns
      ? { columns: columns.map(toSceColumn) }
      : { accessorFn: (s: IntelligenceSummary) => s.sce?.[def.id] ?? '', cell: gridTextCell }),
  } as SummaryColumn;
}

/**
 * Base column set for the new Conflict Summary ServerTable. Column ids are
 * the v3 endpoint's server-side filter/sort properties, so sorting and the
 * filter dialog round-trip without a mapping layer. Cell markup matches the old
 * page, as do data-testids except where a base id carries a `-value-` segment to
 * keep prefix selectors unambiguous against its sibling error/input ids.
 */
export function getSummaryColumns({
  statusLabels,
  callbacks,
}: {
  statusLabels: { [key in PlanStatus]: string };
  callbacks: SummaryColumnCallbacks;
}): SummaryColumn[] {
  const planStatusOptions = Object.keys(statusLabels).map((status) => ({
    value: status,
    label: statusLabels[status as PlanStatus],
  }));

  return [
    {
      id: 'project.projectId',
      size: 100,
      header: 'Project ID',
      accessorFn: (s) => s.project.projectId,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        filterValueConfig: { type: 'text' },
        filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
      },
      cell: (cell) => {
        const id = cell.row.original.project.id;
        const summaryId = cell.row.original.id;
        const value = cell.getValue<string>();

        const onClick = () => {
          capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryProjectOpened, {
            project_id: id,
            summary_id: summaryId,
          });
        };

        return (
          <Link
            to="/projects/$projectId/$tabId"
            params={{ projectId: id, tabId: 'intelligence' }}
            className="underline"
            data-testid={`cell-project-id-${id}`}
            onClick={onClick}
          >
            <GridCellText className="underline">{value}</GridCellText>
          </Link>
        );
      },
    },
    {
      id: 'project.name',
      size: 180,
      header: 'Name',
      accessorFn: (s) => s.project.name,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        filterValueConfig: { type: 'text' },
        filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
      },
      cell: (cell) => {
        const id = cell.row.original.project.id;
        const value = cell.getValue<string>();
        return <GridCellText data-testid={`cell-name-${id}`}>{value}</GridCellText>;
      },
    },
    {
      id: 'project.planStatus',
      header: 'Plan Status',
      accessorFn: (s) => s.project.planStatus,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        filterValueConfig: { type: 'select', options: planStatusOptions },
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
      },
      cell: (cell) => {
        const projectId = cell.row.original.project.id;
        const current = cell.row.original.project.planStatus;
        const summaryId = cell.row.original.id;

        const [update, fetchUpdate] = useCellSave(
          async (value: PlanStatus) => {
            const res = await apiEditProject(projectId, {
              attributes: {
                planStatus: value,
              },
            });

            if (!res.id) throw new Error('Bad response!');
            callbacks.onEdited();

            capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryPlanStatusChanged, {
              project_id: projectId,
              summary_id: summaryId,
              from_status: current,
              to_status: value,
            });
          },
          // useAsyncFn defaults deps to [], which would freeze these values at
          // the cell instance's first render.
          [projectId, summaryId, current],
        );

        if (update.loading) return <CellSkeleton />;

        return (
          <CellShell>
            <div className="flex items-center gap-1">
              <Select
                aria-label="Plan status"
                options={planStatusOptions}
                value={current}
                onValueChange={(v) => v && fetchUpdate(v as PlanStatus)}
                data-testid={`cell-plan-status-value-${projectId}`}
              />
              {update.error && <CellError data-testid={`cell-plan-status-error-${projectId}`} />}
            </div>
          </CellShell>
        );
      },
    },
    {
      id: 'project.constructionYear',
      header: 'Construction Year',
      size: 130,
      accessorFn: (s) => s.project.constructionYear,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        filterValueConfig: { type: 'text' },
        filterOperatorTypeConfig: OPERATOR_TYPES.COMPARISON,
      },
      cell: (cell) => (
        <InlineDateCell cell={cell} field="year" testIdPrefix="cell-construction-year" callbacks={callbacks} />
      ),
    },
    {
      id: 'project.constructionStartDate',
      header: 'Construction Start Date',
      accessorFn: (s) => s.project.constructionStartDate?.toLocaleString() || '',
      enableSorting: true,
      enableColumnFilter: true,
      size: 164,
      meta: {
        filterValueConfig: { type: 'date' },
        filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
      },
      cell: (cell) => (
        <InlineDateCell
          cell={cell}
          field="startDate"
          testIdPrefix="cell-construction-start-date"
          callbacks={callbacks}
        />
      ),
    },
    {
      id: 'schedule.recommendation',
      header: 'Recommendation',
      accessorFn: (s) => {
        return getSummaryValue({
          summary: s,
          value: recommendationLabels[s.schedule.recommendation],
        });
      },
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        filterValueConfig: {
          type: 'select',
          options: Object.values(ConflictRec).map((rec) => ({ value: rec, label: recommendationLabels[rec] })),
        },
        filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
        headerTooltip: (
          <div>Recommendation to accelerate or defer the project timeline based on schedule conflicts.</div>
        ),
      },
      cell: (cell) => {
        const s = cell.row.original;
        const projectId = s.project.id;
        const r = s.schedule.recommendation;
        const warning = useMemo(() => hasWarning(s.undigitizedAgencies), [s.undigitizedAgencies]);
        const cellTestId = `cell-recommendation-${projectId}`;

        return (
          <CellShell>
            <ComputeStateCell summary={s} staleValue={cell.getValue() as string | undefined} data-testid={cellTestId}>
              {() => {
                if (warning) {
                  return (
                    <BadgeCell
                      color="grey"
                      label="-"
                      aria-label={rowBadgeLabel('Recommendation', '-', s.project.name, 'Not available')}
                      tooltip={{
                        title: (
                          <span className="flex items-center gap-1">
                            <Icon icon="luCloudAlert" size={14} className="shrink-0" />
                            NOTE:
                          </span>
                        ),
                        description:
                          'Recommendation not available as this project contains non-digitized data for some agencies.',
                      }}
                      data-testid={cellTestId}
                    />
                  );
                }

                const color = recommendationChipVariant(r);
                const projectStart = s.project.constructionStartDate;
                const projectEnd = s.project.constructionEndDate;
                const start = s.schedule.conflictsStart;
                const end = s.schedule.conflictsEnd;
                let title = cell.getValue<string>();

                if (r === ConflictRec.Accelerate) {
                  if (start && projectStart && projectEnd) {
                    const projectDuration = projectEnd.diff(projectStart);
                    const rollback = start.minus(projectDuration);
                    if (rollback) {
                      title += ` to ${rollback.toLocaleString()}`;
                    }
                  }
                } else if (r === ConflictRec.Defer) {
                  if (end) {
                    title += ` to ${end?.toLocaleString()}`;
                  }
                }

                const tooltip = {
                  title,
                  description: (
                    <div>
                      View conflict details{' '}
                      <Link
                        className="text-blue-50 underline hover:text-blue-60"
                        to="/projects/$projectId/$tabId"
                        params={{ projectId, tabId: 'intelligence' }}
                      >
                        here
                      </Link>
                      .
                    </div>
                  ),
                };

                return (
                  <BadgeCell
                    color={color}
                    label={cell.getValue<string>()}
                    tooltip={tooltip}
                    aria-label={rowBadgeLabel('Recommendation', cell.getValue<string>(), s.project.name)}
                    data-testid={cellTestId}
                  />
                );
              }}
            </ComputeStateCell>
          </CellShell>
        );
      },
    },
    {
      id: 'project.projectManager.name',
      header: 'PM',
      accessorFn: (s) => s.project?.projectManager?.name || '',
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        filterValueConfig: { type: 'text' },
        filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
      },
      cell: (cell) => {
        const id = cell.row.original.project.id;
        const value = cell.getValue<string>();
        return <GridCellText data-testid={`cell-pm-${id}`}>{value}</GridCellText>;
      },
    },
    {
      id: 'notes',
      header: 'Notes',
      accessorKey: 'notes',
      minSize: 200,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        defaultVisible: false,
        filterValueConfig: { type: 'text' },
        filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
      },
      cell: (cell) => {
        const summaryId = cell.row.original.id;
        const projectId = cell.row.original.project.id;
        const initialValue = cell.getValue<string>();

        async function onCommit(notes: string) {
          const res = await apiUpdateSummary(summaryId, { notes });
          if (!res.id) throw new Error('Bad response updating summary notes!');
          callbacks.onEdited();

          capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryNotesUpdated, {
            summary_id: summaryId,
            notes_length: notes.length,
          });
        }

        return (
          <CellShell>
            <FreeFormTextCell
              initialValue={initialValue}
              onCommit={onCommit}
              data-testid={`cell-notes-value-${projectId}`}
              inputTestId={`cell-notes-input-${projectId}`}
            />
          </CellShell>
        );
      },
    },
    {
      id: 'projectConflicts',
      header: 'Project Conflicts',
      columns: [
        {
          id: 'conflictArea',
          header: 'Conflict Area (sq. ft.)',
          accessorFn: (summary) =>
            getSummaryValue({
              summary,
              value: getConflictArea(summary),
            }),
          enableSorting: true,
          enableColumnFilter: true,
          size: 160,
          meta: {
            filterValueConfig: { type: 'text' },
            filterOperatorTypeConfig: OPERATOR_TYPES.COMPARISON,
            headerTooltip: (
              <div>
                The aggregate area of all <span className="font-semibold">paving projects</span>,{' '}
                <span className="font-semibold">paving moratoria</span>, and{' '}
                <span className="font-semibold">Capital Improvement Projects</span> that overlap this project.
              </div>
            ),
          },
          cell: (cell) => {
            const agencies = cell.row.original.undigitizedAgencies;
            const warning = useMemo(() => hasWarning(agencies), [agencies]);
            const projectId = cell.row.original.project.id;

            return (
              <CellShell>
                <Cell
                  warning={warning}
                  value={Math.round(cell.getValue<number>()).toLocaleString()}
                  summary={cell.row.original}
                  data-testid={`cell-conflict-area-${projectId}`}
                />
              </CellShell>
            );
          },
        },
        {
          id: 'percentConflict',
          header: '% Conflict',
          accessorFn: (s) => {
            const a = getConflictArea(s);
            let value = 0;

            if (a || a === 0) {
              const denom = s.rightOfWayArea;

              if (denom) {
                value = Math.round((a / denom) * 100);
              }
            }

            return getSummaryValue({ summary: s, value });
          },
          // Server sorts/filters via the percentConflict propertyOverride (same formula as the accessor).
          enableSorting: true,
          enableColumnFilter: true,
          size: 102,
          meta: {
            filterValueConfig: { type: 'text' },
            filterOperatorTypeConfig: OPERATOR_TYPES.COMPARISON,
            headerTooltip: (
              <div>
                The percent overlap between the aggregate <span className="font-semibold">conflict area</span> and the
                project's <span className="font-semibold">right of way area</span>.
              </div>
            ),
          },
          cell: (cell) => {
            const summary = cell.row.original;
            const agencies = summary.undigitizedAgencies;
            const warning = useMemo(() => hasWarning(agencies), [agencies]);
            const projectId = summary.project.id;
            return (
              <CellShell>
                <Cell
                  warning={warning}
                  value={formatPercentConflict(getConflictArea(summary), summary.rightOfWayArea)}
                  summary={cell.row.original}
                  data-testid={`cell-percent-conflict-${projectId}`}
                />
              </CellShell>
            );
          },
        },
        {
          id: 'conflictDetail',
          header: 'Conflict Detail',
          accessorFn: (s) => (s.conflictWindow ? formatConflictDetail(s.conflictWindow.counts) : ''),
          enableSorting: true,
          enableColumnFilter: true,
          size: 240,
          meta: {
            filterValueConfig: {
              type: 'select',
              options: [
                { value: 'Paving', label: 'Paving' },
                { value: 'Moratoria', label: 'Moratoria' },
                { value: 'CIP', label: 'CIP' },
                { value: 'No Conflicts', label: 'No Conflicts' },
              ],
            },
            filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
          },
          cell: (cell) => {
            const s = cell.row.original;
            const testId = `cell-conflict-detail-${s.project.id}`;
            return (
              <CellShell>
                <ComputeStateCell summary={s} staleValue={cell.getValue<string>()} data-testid={testId}>
                  {() => {
                    const counts = s.conflictWindow?.counts;
                    const chips = [
                      ...(counts?.paving ? [{ label: `${counts.paving} Paving`, color: 'orange' as const }] : []),
                      ...(counts?.moratoria
                        ? [
                            {
                              label: `${counts.moratoria} ${counts.moratoria === 1 ? 'Moratorium' : 'Moratoria'}`,
                              color: 'red' as const,
                            },
                          ]
                        : []),
                      ...(counts?.cip ? [{ label: `${counts.cip} CIP`, color: 'yellow' as const }] : []),
                    ];
                    if (chips.length === 0) {
                      return <BadgeCell color="green" label="No Conflicts" data-testid={testId} />;
                    }
                    // Grid rows are fixed-height, so the chips stay on one clipped line;
                    // the card carries the full breakdown for anything cut off.
                    const breakdownRows = [
                      ...(counts?.paving ? [{ label: 'Paving', value: counts.paving }] : []),
                      ...(counts?.moratoria ? [{ label: 'Moratoria', value: counts.moratoria }] : []),
                      ...(counts?.cip ? [{ label: 'CIP', value: counts.cip }] : []),
                    ];
                    return (
                      <BadgePopover
                        cardLabel="Conflict detail breakdown"
                        content={<DetailCard rows={breakdownRows} className="w-72" />}
                      >
                        <button
                          type="button"
                          className="flex max-w-full rounded-sm"
                          aria-label={rowBadgeLabel(
                            'Conflict Detail',
                            chips.map((chip) => chip.label).join(', '),
                            s.project.name,
                          )}
                          data-testid={testId}
                        >
                          {/* Clips the chips here, so the button's 24 px pointer target stays unclipped. */}
                          <span className="flex items-center gap-1 overflow-hidden">
                            {chips.map((chip) => (
                              <span key={chip.label} className="shrink-0">
                                <BadgeCell color={chip.color} label={chip.label} />
                              </span>
                            ))}
                          </span>
                        </button>
                      </BadgePopover>
                    );
                  }}
                </ComputeStateCell>
              </CellShell>
            );
          },
        },
        {
          id: 'scheduleOverlap',
          header: 'Schedule Overlap',
          accessorFn: (s) => (s.conflictWindow ? formatScheduleOverlap(s.conflictWindow) : ''),
          enableSorting: true,
          enableColumnFilter: false,
          size: 200,
          cell: (cell) => {
            const s = cell.row.original;
            const testId = `cell-schedule-overlap-${s.project.id}`;
            return (
              <CellShell>
                <ComputeStateCell summary={s} staleValue={cell.getValue<string>()} data-testid={testId}>
                  {() => {
                    if (!s.conflictWindow) {
                      return <GridCellText data-testid={testId}>—</GridCellText>;
                    }
                    const { duration, range } = formatScheduleOverlapParts(s.conflictWindow);
                    return (
                      <div className="flex items-center gap-1.5" data-testid={testId}>
                        <BadgeCell color="grey" label={duration} />
                        <GridCellText>{range}</GridCellText>
                      </div>
                    );
                  }}
                </ComputeStateCell>
              </CellShell>
            );
          },
        },
      ],
    },
    {
      id: 'rightOfWayInformation',
      header: 'Right of Way Information',
      columns: [
        {
          id: 'jurisdictionCount',
          header: 'Jurisdiction Count',
          accessorFn: (summary) =>
            getSummaryValue({
              summary,
              value: getJurisdictionCount(summary),
            }),
          // Server sorts/filters via the jurisdictionCount propertyOverride (same sum as the accessor).
          enableSorting: true,
          enableColumnFilter: true,
          size: 146,
          meta: {
            filterValueConfig: { type: 'text' },
            filterOperatorTypeConfig: OPERATOR_TYPES.COMPARISON,
            headerTooltip: (
              <div>
                The number of intersecting cities, counties, and state department of transportation districts in this
                project area.
              </div>
            ),
          },
          cell: (cell) => {
            const value = cell.getValue<number>()?.toLocaleString() ?? '';
            const cities = cell.row.original.incorporatedPlacesCount;
            const counties = cell.row.original.countyCount;
            const dotDistricts = cell.row.original.landUseTransportationDistrictBoundariesCount;
            const projectId = cell.row.original.project.id;

            const tooltipRows = [
              { label: 'Cities', value: cities },
              { label: 'Counties', value: counties },
              { label: 'DOT Districts', value: dotDistricts },
            ];

            return (
              <CellShell>
                <Cell
                  value={value}
                  summary={cell.row.original}
                  tooltipRows={tooltipRows}
                  data-testid={`cell-jurisdiction-count-${projectId}`}
                />
              </CellShell>
            );
          },
        },
        {
          id: 'crossingsCount',
          header: 'Crossings Count',
          columns: [
            {
              id: 'landUseRailroadsCount',
              header: 'Rail',
              accessorFn: (summary) =>
                getSummaryValue({
                  summary,
                  value: summary.landUseRailroadsCount,
                }),
              enableSorting: true,
              enableColumnFilter: true,
              size: 85,
              meta: {
                filterValueConfig: { type: 'text' },
                filterOperatorTypeConfig: OPERATOR_TYPES.COMPARISON,
                headerTooltip: <div>The number of rail crossings in this project area.</div>,
              },
              cell: (cell) => {
                const projectId = cell.row.original.project.id;
                return (
                  <CellShell>
                    <Cell
                      value={cell.getValue<number>()?.toLocaleString() ?? ''}
                      summary={cell.row.original}
                      data-testid={`cell-rail-count-${projectId}`}
                    />
                  </CellShell>
                );
              },
            },
            {
              id: 'transitLinesCount',
              header: 'Transit',
              accessorFn: (summary) => getSummaryValue({ summary, value: summary.transitLinesCount }),
              enableSorting: true,
              enableColumnFilter: true,
              size: 85,
              meta: {
                filterValueConfig: { type: 'text' },
                filterOperatorTypeConfig: OPERATOR_TYPES.COMPARISON,
                headerTooltip: <div>The number of transit crossings in this project area.</div>,
              },
              cell: (cell) => {
                const projectId = cell.row.original.project.id;
                return (
                  <CellShell>
                    <Cell
                      value={cell.getValue<number>()?.toLocaleString() ?? ''}
                      summary={cell.row.original}
                      data-testid={`cell-transit-count-${projectId}`}
                    />
                  </CellShell>
                );
              },
            },
            {
              id: 'landUseRoadsBufferCount',
              header: 'Roads',
              accessorFn: (summary) =>
                getSummaryValue({
                  summary,
                  value: summary.landUseRoadsBufferCount,
                }),
              enableSorting: true,
              enableColumnFilter: true,
              size: 85,
              meta: {
                filterValueConfig: { type: 'text' },
                filterOperatorTypeConfig: OPERATOR_TYPES.COMPARISON,
                headerTooltip: <div>The number of road crossings in this project area.</div>,
              },
              cell: (cell) => {
                const projectId = cell.row.original.project.id;
                return (
                  <CellShell>
                    <Cell
                      value={cell.getValue<number>()?.toLocaleString() ?? ''}
                      summary={cell.row.original}
                      data-testid={`cell-roads-count-${projectId}`}
                    />
                  </CellShell>
                );
              },
            },
          ],
        },
      ],
    },
  ];
}

/** Construction End Date column (inserted after the start date when the intelligence quick-wins flag is on). */
export function getConstructionEndDateColumn(callbacks: SummaryColumnCallbacks): SummaryColumn {
  return {
    id: 'project.constructionEndDate',
    header: 'Construction End Date',
    size: 160,
    accessorFn: (s) => s.project.constructionEndDate?.toLocaleString() || '',
    enableSorting: true,
    enableColumnFilter: true,
    meta: {
      filterValueConfig: { type: 'date' },
      filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
    },
    cell: (cell) => (
      <InlineDateCell cell={cell} field="endDate" testIdPrefix="cell-construction-end-date" callbacks={callbacks} />
    ),
  };
}

/** Program Manager / Sub-Program Manager columns (inserted after PM when the program-manager flag is on). */
export function getProgramManagerColumns(): SummaryColumn[] {
  return [
    {
      id: 'project.programManager.name',
      header: 'Program Manager',
      accessorFn: (s) => s.project?.programManager?.name || '',
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        filterValueConfig: { type: 'text' },
        filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
      },
      cell: (cell) => {
        const id = cell.row.original.project.id;
        const value = cell.getValue<string>();
        return <GridCellText data-testid={`cell-program-manager-${id}`}>{value}</GridCellText>;
      },
    },
    {
      id: 'project.subProgramManager.name',
      header: 'Sub-Program Manager',
      size: 158,
      accessorFn: (s) => s.project?.subProgramManager?.name || '',
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        filterValueConfig: { type: 'text' },
        filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
      },
      cell: (cell) => {
        const id = cell.row.original.project.id;
        const value = cell.getValue<string>();
        return <GridCellText data-testid={`cell-sub-program-manager-${id}`}>{value}</GridCellText>;
      },
    },
  ];
}

/** PG&E Division/Region columns (inserted after Name for pge-configured orgs). */
export function getPgeRegionAndDivisionColumns(): SummaryColumn[] {
  return [
    {
      id: 'pge.division',
      size: 150,
      header: 'Division',
      accessorFn: (s) => s.pge?.division || '',
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        filterValueConfig: { type: 'text' },
        filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
      },
      cell: ({ getValue }) => <GridCellText className="capitalize">{getValue<string>().toLowerCase()}</GridCellText>,
    },
    {
      id: 'pge.region',
      size: 150,
      header: 'Region',
      accessorFn: (s) => s.pge?.region || '',
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        filterValueConfig: { type: 'text' },
        filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
      },
      cell: ({ getValue }) => <GridCellText className="capitalize">{getValue<string>().toLowerCase()}</GridCellText>,
    },
  ];
}

/** PG&E Gas Information group (inserted before Right of Way Information for pge-configured orgs). */
export function getPgeGasInformationColumns(): SummaryColumn[] {
  return [
    {
      id: 'gasInformation',
      header: 'Gas Information',
      columns: [
        {
          id: 'gasServiceCount',
          header: 'Gas Service Count',
          accessorFn: (summary) => getSummaryValue({ summary, value: summary.gasServiceCount }),
          enableSorting: true,
          enableColumnFilter: true,
          meta: {
            filterValueConfig: { type: 'text' },
            filterOperatorTypeConfig: OPERATOR_TYPES.COMPARISON,
          },
          cell: (cell) => {
            return (
              <CellShell>
                <Cell value={cell.getValue<number | string>()?.toLocaleString() || ''} summary={cell.row.original} />
              </CellShell>
            );
          },
        },
        {
          id: 'gasDistributionLength',
          header: 'Gas Distribution Length (mi.)',
          accessorFn: (summary) =>
            getSummaryValue({
              summary,
              value: toMiles(summary.gasDistributionLength || 0),
            }),
          // Server sorts/filters in display units: the gasDistributionLength
          // propertyOverride converts feet to miles (2 decimals), matching toMiles.
          enableSorting: true,
          enableColumnFilter: true,
          meta: {
            filterValueConfig: { type: 'text' },
            filterOperatorTypeConfig: OPERATOR_TYPES.COMPARISON,
          },
          cell: (cell) => {
            return (
              <CellShell>
                <Cell value={cell.getValue<number | string>()?.toLocaleString() || ''} summary={cell.row.original} />
              </CellShell>
            );
          },
        },
      ],
    },
  ];
}

/**
 * SCE columns, fully server-backed: sort/filter run on the v3 endpoint's SQL
 * expressions and cells render its resolved `sce` payload values. Only the
 * column skeleton (ids, headers, grouping, tooltips) comes from the legacy defs.
 */
export function getSceColumns(): SummaryColumn[] {
  return sceColumns.map(toSceColumn);
}
